import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { PartialLockDiagram } from '../components/prescription/PartialLockDiagram';
import { useAppState } from '../hooks/useAppState';
import { getFirmById, getFirmAccountBySize, getPlanById } from '../data/firms';
import { tclTiers, smogTiers, getTCLRules, getSMOGRules, getTCLExecuteSteps, smogExecuteSteps, smogPartialLock } from '../data/strategies';
import { calculateRiskPerTrade, calculateExpectancy, getLifebarPercent } from '../utils/calculations';
import { formatDate, formatCurrency, formatPercent, formatR } from '../utils/formatting';
import { toast } from '../components/shared/Toast';
import { Save, Copy, ClipboardCheck, AlertTriangle, ChevronDown, Shield, Swords } from 'lucide-react';
import type { Prescription as PrescriptionType, TCLTierName, SMOGTierName, TierName, PrescriptionRule, ExecuteStep, PartialLock as PartialLockType } from '../types';

// ─── Warhammer icon (custom SVG) ────────────────────────────────────────────

function WarhammerIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      {/* Handle */}
      <line x1="12" y1="22" x2="12" y2="10" />
      {/* Hammer head - heavy rectangular warhammer */}
      <path d="M5 3h14v7H5z" />
      {/* Spike on top */}
      <line x1="12" y1="3" x2="12" y2="1" />
      {/* Cross guard / grip band */}
      <line x1="9" y1="13" x2="15" y2="13" />
    </svg>
  );
}

// ─── Tier card config ────────────────────────────────────────────────────────

type TierIcon = typeof Shield | typeof WarhammerIcon;

interface TierCardData {
  tier: TierName;
  label: string;
  riskLabel: string;
  color: string;           // primary accent
  colorRgb: string;        // for rgba()
  icon: TierIcon;
  lifebarPct: number;
  riskPerTrade: number;
  rules: PrescriptionRule[];
  executeSteps: ExecuteStep[];
  partialLock: PartialLockType | null;
}

const TCL_TIER_META: { tier: TCLTierName; label: string; riskLabel: string; color: string; colorRgb: string; icon: TierIcon }[] = [
  { tier: 'Shield', label: 'Shield', riskLabel: 'Low Risk', color: '#3DBF7A', colorRgb: '61, 191, 122', icon: Shield },
  { tier: 'Sword', label: 'Sword', riskLabel: 'Medium Risk', color: '#E8943A', colorRgb: '232, 148, 58', icon: Swords },
  { tier: 'Hammer', label: 'Hammer', riskLabel: 'High Risk', color: '#E84040', colorRgb: '232, 64, 64', icon: WarhammerIcon },
];

const SMOG_TIER_META: { tier: SMOGTierName; label: string; riskLabel: string; color: string; colorRgb: string; icon: TierIcon }[] = [
  { tier: 'Sling', label: 'Sling', riskLabel: 'Low Risk', color: '#3DBF7A', colorRgb: '61, 191, 122', icon: Shield },
  { tier: 'Longbow', label: 'Longbow', riskLabel: 'Medium Risk', color: '#E8943A', colorRgb: '232, 148, 58', icon: Swords },
  { tier: 'Crossbow', label: 'Crossbow', riskLabel: 'High Risk', color: '#E84040', colorRgb: '232, 64, 64', icon: WarhammerIcon },
];

// ─── Fragment animation origins ─────────────────────────────────────────────

const fragments = [
  { fromX: -70, fromY: -50, fromR: -6 },
  { fromX: 90, fromY: -35, fromR: 10 },
  { fromX: -50, fromY: 70, fromR: -5 },
  { fromX: 80, fromY: 55, fromR: 8 },
  { fromX: -60, fromY: 40, fromR: -4 },
];

function FragmentReveal({ children, index }: { children: React.ReactNode; index: number }) {
  const f = fragments[index % fragments.length];
  return (
    <motion.div
      initial={{ x: f.fromX, y: f.fromY, rotate: f.fromR, opacity: 0, scale: 0.7 }}
      animate={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 1 }}
      transition={{ type: 'spring' as const, stiffness: 220, damping: 22, delay: 0.15 + index * 0.1 }}
    >
      {children}
    </motion.div>
  );
}

function TierDivider({ colorRgb }: { colorRgb: string }) {
  return (
    <motion.div
      className="h-px"
      style={{ background: `linear-gradient(to right, transparent, rgba(${colorRgb}, 0.2), transparent)` }}
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ delay: 0.3, duration: 0.5 }}
    />
  );
}

// ─── Expanded card content ──────────────────────────────────────────────────

function TierCardExpanded({
  card,
  rx,
  isSMOG,
  onSave,
  onCopy,
}: {
  card: TierCardData;
  rx: PrescriptionType;
  isSMOG: boolean;
  onSave: () => void;
  onCopy: () => void;
}) {
  const expectancy = rx.stats.expectancy ?? 0;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div className="flex flex-col gap-5 pt-5">
        <TierDivider colorRgb={card.colorRgb} />

        {/* Risk Per Trade */}
        <FragmentReveal index={0}>
          <div
            className="rounded-lg p-5"
            style={{
              background: `rgba(${card.colorRgb}, 0.03)`,
              border: `1px solid rgba(${card.colorRgb}, 0.08)`,
            }}
          >
            <span className="text-[10px] font-mono text-white/30 uppercase tracking-wider block mb-1">
              Risk Per Trade
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-mono font-bold text-white">
                {formatCurrency(card.riskPerTrade)}
              </span>
              <span className="text-base font-mono text-white/40">
                {card.lifebarPct.toFixed(1)}%
              </span>
            </div>
          </div>
        </FragmentReveal>

        <TierDivider colorRgb={card.colorRgb} />

        {/* Rules */}
        <FragmentReveal index={1}>
          <h3
            className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3"
            style={{ color: `rgba(${card.colorRgb}, 0.4)` }}
          >
            Rules
          </h3>
          <div className="space-y-2.5">
            {card.rules.map((rule, i) => {
              const isHigh = rule.severity === 'high';
              return (
                <motion.div
                  key={i}
                  className="flex items-start gap-2.5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 + i * 0.06 }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{
                      background: isHigh ? `rgba(${card.colorRgb}, 0.7)` : 'rgba(255,255,255,0.2)',
                    }}
                  />
                  <div>
                    <span
                      className="text-[12px] font-mono font-medium"
                      style={{ color: isHigh ? `rgba(${card.colorRgb}, 0.8)` : 'rgba(255,255,255,0.55)' }}
                    >
                      {rule.name}
                    </span>
                    <p className="text-[11px] font-mono text-white/30 mt-0.5 leading-relaxed">
                      {rule.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </FragmentReveal>

        {/* Partial Lock (SMOG only) */}
        {isSMOG && card.partialLock && (
          <>
            <TierDivider colorRgb={card.colorRgb} />
            <FragmentReveal index={2}>
              <PartialLockDiagram partialLock={card.partialLock} />
            </FragmentReveal>
          </>
        )}

        <TierDivider colorRgb={card.colorRgb} />

        {/* Expectancy */}
        <FragmentReveal index={isSMOG ? 3 : 2}>
          <h3
            className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3"
            style={{ color: `rgba(${card.colorRgb}, 0.4)` }}
          >
            Expectancy
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] font-mono">
            <span className="text-white/30">Win Rate</span>
            <span className="text-white/70 text-right">{formatPercent(rx.stats.winRate)}</span>

            <span className="text-white/30">Avg Winner</span>
            <span className="text-white/70 text-right">{formatR(rx.stats.avgWinner)}</span>

            <span className="text-white/30">Avg Loser</span>
            <span className="text-white/70 text-right">{formatR(rx.stats.avgLoser)}</span>

            <div className="col-span-2 h-px my-1" style={{ background: `rgba(${card.colorRgb}, 0.1)` }} />

            <span className="text-white/40 font-semibold">Expectancy/Trade</span>
            <span className={`text-right font-bold ${expectancy >= 0 ? 'text-[#3DBF7A]' : 'text-[#E84040]'}`}>
              {expectancy >= 0 ? '+' : ''}{formatR(expectancy)}
            </span>
          </div>
        </FragmentReveal>

        <TierDivider colorRgb={card.colorRgb} />

        {/* Execute */}
        <FragmentReveal index={isSMOG ? 4 : 3}>
          <h3
            className="text-[10px] font-mono tracking-[0.2em] uppercase mb-3"
            style={{ color: `rgba(${card.colorRgb}, 0.4)` }}
          >
            Execute
          </h3>
          <div className="flex flex-col gap-0">
            {card.executeSteps.map((step, i) => (
              <motion.div
                key={step.step}
                className="flex items-start gap-3 relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.06 }}
              >
                {i < card.executeSteps.length - 1 && (
                  <div
                    className="absolute left-3 top-7 w-px h-[calc(100%-4px)]"
                    style={{ background: `rgba(${card.colorRgb}, 0.1)` }}
                  />
                )}
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10"
                  style={{
                    background: `rgba(${card.colorRgb}, 0.1)`,
                    border: `1px solid rgba(${card.colorRgb}, 0.2)`,
                  }}
                >
                  <span className="text-[10px] font-bold" style={{ color: `rgba(${card.colorRgb}, 0.7)` }}>
                    {step.step}
                  </span>
                </div>
                <div className="pb-4">
                  <span className="text-[12px] font-mono font-medium text-white/60">{step.title}</span>
                  <p className="text-[11px] font-mono text-white/30 mt-0.5 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </FragmentReveal>

        <TierDivider colorRgb={card.colorRgb} />

        {/* Actions */}
        <motion.div
          className="flex flex-col sm:flex-row gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onSave(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-[11px] font-mono font-semibold tracking-wider uppercase transition-all cursor-pointer"
            style={{
              background: `rgba(${card.colorRgb}, 0.1)`,
              color: `rgba(${card.colorRgb}, 0.8)`,
              border: `1px solid rgba(${card.colorRgb}, 0.2)`,
            }}
          >
            <Save className="w-4 h-4" />
            Save Protocol
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-[11px] font-mono font-semibold tracking-wider uppercase bg-white/[0.03] text-white/45 border border-white/[0.06] hover:bg-white/[0.06] hover:text-white/65 transition-all cursor-pointer"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>
        </motion.div>

        {/* Stale warning */}
        <motion.p
          className="text-[10px] font-mono text-white/15 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {rx.staleWarning}
        </motion.p>
      </div>
    </motion.div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function Prescription() {
  const navigate = useNavigate();
  const { user, savePrescription } = useAppState();
  const [expandedTier, setExpandedTier] = useState<TierName | null>(null);

  // Build tier cards + prescriptions for all tiers
  const { tierCards, failReason } = useMemo<{ tierCards: (TierCardData & { rx: PrescriptionType })[]; failReason: string | null }>(() => {
    if (!user) return { tierCards: [], failReason: 'No user data found. Please sign in and start again.' };
    if (!user.currentStrategy) return { tierCards: [], failReason: 'No strategy selected. Go back to the Assessment step and choose TCL or SMOG.' };
    if (!user.currentFirmId) return { tierCards: [], failReason: 'No prop firm selected. Go back to the Firm step and pick a firm.' };
    if (!user.currentPlanId) return { tierCards: [], failReason: 'No plan selected. Go back to the Firm step and pick a plan.' };

    const firm = getFirmById(user.currentFirmId);
    if (!firm) return { tierCards: [], failReason: `Firm "${user.currentFirmId}" not found in our database. Go back and select a different firm.` };

    const plan = getPlanById(firm.id, user.currentPlanId);
    if (!plan) return { tierCards: [], failReason: `Plan "${user.currentPlanId}" not found for ${firm.name}. Go back and select a different plan.` };

    const account = getFirmAccountBySize(firm.id, plan.id, user.accountSize);
    if (!account) {
      const availableSizes = plan.accounts.map(a => `$${a.size.toLocaleString()}`).join(', ');
      return { tierCards: [], failReason: `${firm.name}'s "${plan.name}" plan doesn't offer a $${user.accountSize.toLocaleString()} account. Available sizes: ${availableSizes}. Go back and select a matching account size.` };
    }

    const strategy = user.currentStrategy;
    const stats = user.stats;
    const expectancy = stats.expectancy ?? calculateExpectancy(stats.winRate, stats.avgWinner, stats.avgLoser);
    const dailyLoss = account.dailyLossLimit ?? account.maxDrawdown;
    const maxDrawdown = account.maxDrawdown;
    const now = new Date().toISOString();

    const metas = strategy === 'TCL' ? TCL_TIER_META : SMOG_TIER_META;

    const cards = metas.map((meta) => {
      const tierDef = strategy === 'TCL'
        ? tclTiers[meta.tier as TCLTierName]
        : smogTiers[meta.tier as SMOGTierName];
      const lifebarPct = tierDef.lifebarPercent;
      const riskPerTrade = calculateRiskPerTrade(user.accountSize, lifebarPct);

      const rules = strategy === 'TCL'
        ? getTCLRules(dailyLoss, maxDrawdown)
        : getSMOGRules(dailyLoss, plan.fundedConsistencyRulePct, riskPerTrade);

      const executeSteps = strategy === 'TCL'
        ? getTCLExecuteSteps(riskPerTrade * 2)
        : smogExecuteSteps;

      const rx: PrescriptionType = {
        id: `rx-${meta.tier}-${Date.now()}`,
        userId: user.id,
        strategy,
        tier: meta.tier as TierName,
        firmId: firm.id,
        firmName: firm.name,
        accountSize: user.accountSize,
        riskPerTrade,
        riskPerTradePercent: lifebarPct,
        stats: { ...stats, expectancy },
        rules,
        partialLock: strategy === 'SMOG' ? smogPartialLock : null,
        executeSteps,
        generatedAt: now,
        staleWarning: `Generated ${formatDate(now)}. Regenerate if your firm, account size, or trading stats change.`,
      };

      return {
        ...meta,
        lifebarPct,
        riskPerTrade,
        rules,
        executeSteps,
        partialLock: strategy === 'SMOG' ? smogPartialLock : null,
        rx,
      } as TierCardData & { rx: PrescriptionType };
    });

    return { tierCards: cards, failReason: null };
  }, [user]);

  // All collapsed by default until user selects one
  const activeExpanded = expandedTier;

  // ─── Error state ────────────────────────────────────────────────────────

  if (failReason) {
    return (
      <PageWrapper warmth={0.95}>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16 px-8">
          <Card className="max-w-[520px] text-center">
            <AlertTriangle className="w-10 h-10 text-[#A8B4C0] mx-auto mb-4" />
            <h2 className="text-lg font-bold font-mono tracking-wide text-white/90 mb-3">Unable to generate protocol</h2>
            <p className="text-sm font-mono text-white/40 mb-5 leading-relaxed text-left">
              {failReason}
            </p>
            <Button variant="secondary" onClick={() => navigate('/assess')}>
              Start Over
            </Button>
          </Card>
        </main>
      </PageWrapper>
    );
  }

  // ─── Handlers ───────────────────────────────────────────────────────────

  function handleSave(rx: PrescriptionType) {
    savePrescription(rx);
    toast('Protocol saved to your dashboard.');
  }

  function handleCopy(rx: PrescriptionType) {
    const text = [
      `PALADIN PROTOCOL`,
      `${rx.strategy} — ${rx.tier} Tier`,
      `${rx.firmName} | $${rx.accountSize.toLocaleString()}`,
      ``,
      `Risk Per Trade: $${rx.riskPerTrade.toLocaleString()} (${rx.riskPerTradePercent}%)`,
      ``,
      `RULES:`,
      ...rx.rules.map(r => `- ${r.name}: ${r.description}`),
      ``,
      `EXECUTE:`,
      ...rx.executeSteps.map(s => `${s.step}. ${s.title}: ${s.description}`),
      ``,
      rx.staleWarning,
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      toast('Protocol copied. Ready to paste.');
    });
  }

  const isSMOG = user?.currentStrategy === 'SMOG';
  const firm = user?.currentFirmId ? getFirmById(user.currentFirmId) : null;
  const acctLabel = user?.accountSize
    ? user.accountSize >= 1000 ? `$${(user.accountSize / 1000).toFixed(0)}K` : `$${user.accountSize}`
    : '';

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <PageWrapper warmth={0.95}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={6} />

        <div className="w-full max-w-[540px] mx-auto px-8 pb-16">
          {/* Context header */}
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-[10px] font-mono text-[#A8B4C0]/50 tracking-[0.2em] uppercase">
              Paladin Protocol
            </p>
            <p className="text-[11px] font-mono text-white/25 mt-1">
              {user?.currentStrategy} &middot; {firm?.name} &middot; {acctLabel}
            </p>
          </motion.div>

          {/* Tier cards */}
          <div className="flex flex-col gap-4">
            {tierCards.map((card, cardIndex) => {
              const isExpanded = activeExpanded === card.tier;
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.tier}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + cardIndex * 0.1 }}
                >
                  <div className="relative">
                    {/* Breathing glow (only on expanded) */}
                    {isExpanded && (
                      <motion.div
                        className="absolute -inset-3 rounded-2xl pointer-events-none z-0"
                        animate={{
                          boxShadow: [
                            `0 0 30px rgba(${card.colorRgb}, 0.06), 0 0 60px rgba(${card.colorRgb}, 0.03)`,
                            `0 0 50px rgba(${card.colorRgb}, 0.12), 0 0 100px rgba(${card.colorRgb}, 0.05)`,
                            `0 0 30px rgba(${card.colorRgb}, 0.06), 0 0 60px rgba(${card.colorRgb}, 0.03)`,
                          ],
                        }}
                        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                      />
                    )}

                    {/* SVG border trace (only on expanded) */}
                    {isExpanded && (
                      <svg
                        className="absolute inset-0 w-full h-full pointer-events-none z-20 rounded-xl"
                        viewBox="0 0 540 900"
                        preserveAspectRatio="none"
                      >
                        <motion.rect
                          x="1" y="1" width="538" height="898" rx="12"
                          fill="none" stroke={card.color} strokeWidth="1"
                          strokeDasharray="2876"
                          initial={{ strokeDashoffset: 2876, opacity: 0.6 }}
                          animate={{ strokeDashoffset: 0, opacity: 0.35 }}
                          transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.2 }}
                        />
                      </svg>
                    )}

                    {/* Card body */}
                    <div
                      className={`
                        relative bg-white/[0.02] border rounded-xl backdrop-blur-md overflow-hidden z-10
                        ${isExpanded ? 'border-white/[0.06]' : 'border-white/[0.04]'}
                        cursor-pointer
                      `}
                      onClick={() => {
                        setExpandedTier(isExpanded ? null : card.tier);
                      }}
                    >
                      {/* Inner gradient */}
                      {isExpanded && (
                        <div
                          className="absolute inset-0 rounded-xl pointer-events-none"
                          style={{ background: `radial-gradient(ellipse at 50% 0%, rgba(${card.colorRgb}, 0.04) 0%, transparent 55%)` }}
                        />
                      )}

                      <div className="relative z-10 p-6">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center"
                              style={{
                                background: `rgba(${card.colorRgb}, 0.1)`,
                                border: `1px solid rgba(${card.colorRgb}, 0.2)`,
                              }}
                            >
                              <Icon className="w-5 h-5" style={{ color: card.color }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-mono font-semibold text-white/80">
                                  {card.label}
                                </span>
                                <span
                                  className="text-[9px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full"
                                  style={{
                                    color: card.color,
                                    background: `rgba(${card.colorRgb}, 0.1)`,
                                    border: `1px solid rgba(${card.colorRgb}, 0.15)`,
                                  }}
                                >
                                  {card.riskLabel}
                                </span>
                              </div>
                              <span className="text-[11px] font-mono text-white/30 mt-0.5 block">
                                {card.lifebarPct.toFixed(1)}% &middot; {formatCurrency(card.riskPerTrade)}/trade
                              </span>
                            </div>
                          </div>

                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.25 }}
                          >
                            <ChevronDown className="w-4 h-4 text-white/25" />
                          </motion.div>
                        </div>

                        {/* Expanded content */}
                        <AnimatePresence>
                          {isExpanded && (
                            <TierCardExpanded
                              card={card}
                              rx={card.rx}
                              isSMOG={isSMOG}
                              onSave={() => handleSave(card.rx)}
                              onCopy={() => handleCopy(card.rx)}
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Legal */}
          <motion.div
            className="mt-6 px-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <p className="text-[10px] font-mono text-white/15 leading-relaxed text-center">
              Paladin is an educational and informational tool. It is not financial or investment advice.
              All content is provided for educational purposes only. You assume full responsibility for
              any trading decisions you make. Prop firm rules change — verify current rules directly
              with your firm before every challenge.
            </p>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
}
